import * as THREE from 'three';

/**
 * Utilitaires pour le rendu et l'affichage 3D
 */
export class RenderingUtils {
    
    /**
     * Applique une matrice de transformation à un modèle 3D
     */
    static applyMatrix(model, matrix, options = {}) {
        const {
            decompose = false, 
            scale = 1, 
            position = null, 
            rotation = null, 
            useMatrixPosition = true,
            useMatrixRotation = true,
            lerp = null // Nouveau paramètre pour l'interpolation
        } = options;
        
        if (!model || !matrix) return;

        // Convert matrix to THREE.Matrix4 if needed
        let mat;
        if (matrix instanceof THREE.Matrix4) {
            mat = matrix.clone();
        } else if (Array.isArray(matrix.data) && matrix.data.length === 16) {
            mat = new THREE.Matrix4();
            mat.fromArray(matrix.data);
        } else {
            return;
        }

        // Apply scale to the matrix (multiplicative)
        mat.scale(new THREE.Vector3(scale, scale, scale));

        // Si lerp est fourni, utiliser l'interpolation
        if (lerp) {
            // Décomposer la matrice cible
            const targetPosition = new THREE.Vector3();
            const targetQuaternion = new THREE.Quaternion();
            const targetScale = new THREE.Vector3();
            mat.decompose(targetPosition, targetQuaternion, targetScale);

            // Override position/rotation si fournis et si les flags correspondants sont false
            if (!useMatrixPosition && position) {
                targetPosition.set(position.x, position.y, position.z);
            }
            if (!useMatrixRotation && rotation) {
                // Vérifier si rotation est un quaternion ou un Euler
                if (rotation.isQuaternion) {
                    targetQuaternion.copy(rotation);
                } else if (rotation.x !== undefined && rotation.y !== undefined && rotation.z !== undefined) {
                    // Si c'est un Euler ou un objet avec x,y,z
                    if (rotation.w !== undefined) {
                        // C'est probablement un quaternion déguisé
                        targetQuaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
                    } else {
                        // C'est un Euler
                        targetQuaternion.setFromEuler(new THREE.Euler(rotation.x, rotation.y, rotation.z));
                    }
                }
            }

            // S'assurer que matrixAutoUpdate est activé pour permettre l'interpolation
            model.matrixAutoUpdate = true;

            // Appliquer l'interpolation
            model.position.lerp(targetPosition, lerp.position || 0.1);
            model.quaternion.slerp(targetQuaternion, lerp.rotation || 0.1);
            model.scale.lerp(targetScale, lerp.scale || 0.1);
        } else {
            // Mode sans lerp (comportement original)
            // Disable auto-update to use the manual matrix
            model.matrixAutoUpdate = false;

            // Reset local transforms
            model.position.set(0, 0, 0);
            model.rotation.set(0, 0, 0);
            model.scale.set(1, 1, 1);

            // Apply the matrix directly
            model.matrix.copy(mat);

            // Override position if provided and useMatrixPosition is false
            if (!useMatrixPosition && position) {
                model.matrix.setPosition(new THREE.Vector3(position.x, position.y, position.z));
            }

            // Override rotation if provided and useMatrixRotation is false
            if (!useMatrixRotation && rotation) {
                const rotEuler = new THREE.Euler(rotation.x, rotation.y, rotation.z);
                const rotQuat = new THREE.Quaternion().setFromEuler(rotEuler);
                const currentPos = new THREE.Vector3();
                const currentQuat = new THREE.Quaternion();
                const currentScale = new THREE.Vector3();
                model.matrix.decompose(currentPos, currentQuat, currentScale);
                model.matrix.compose(currentPos, rotQuat, currentScale);
            }
          
            // Synchronize position/rotation/scale properties with the matrix
            model.matrix.decompose(model.position, model.quaternion, model.scale);
            model.rotation.setFromQuaternion(model.quaternion);
        }
    }

    /**
     * Calcule la distance de caméra basée sur la hauteur et le field of view
     */
    static calculateCameraDistance(height, fov) {
        return (height / 2) / Math.tan((fov / 2) * Math.PI / 180);
    }

   
    /**
     * Transforme un objet 3D en overlay en modifiant ses propriétés de rendu
     */
    static makeOverlayObject(object, options = {}) {
        const {
            renderOrder = 1000,
            renderOrderIncrement = 0.1,
            depthTest = false,
            depthWrite = false
        } = options;

        let materialCount = 0;
        let childIndex = 0;

        // Parcourir récursivement tous les enfants de l'objet
        object.traverse((child) => {
            // Assigner des renderOrder légèrement incrémentaux pour éviter le z-fighting
            // (conflit visuel quand deux surfaces sont à la même profondeur)
            child.renderOrder = renderOrder + (childIndex * renderOrderIncrement);
            childIndex++;
            
            // Traiter les matériaux si l'enfant en possède
            if (child.material) {
                // Normaliser en tableau pour traitement uniforme
                const materials = Array.isArray(child.material) ? child.material : [child.material];
              //  console.log(child.material)
                // Détecter la présence de ShaderMaterial (incompatible avec les overlays)
                const hasShaderMaterial = materials.some(mat => mat.type === 'ShaderMaterial');

                // Masquer complètement les mesh avec ShaderMaterial car ils causent des problèmes
                if (hasShaderMaterial && child.isMesh) {
                    child.visible = false;
                    return; // Passer au prochain enfant
                }

                // Traiter tous les matériaux avec la même logique
                materials.forEach((material) => {
                 
                    // Appliquer les propriétés d'overlay directement
                    material.depthWrite = depthWrite; // Contrôle l'écriture dans le buffer de profondeur
                    material.depthTest = depthTest;   // Contrôle le test de profondeur pour l'occlusion
               
                     
                    materialCount++;
                });
            }

            // Assigner le renderOrder aux mesh visibles pour contrôler l'ordre d'affichage
            if (child.isMesh && child.visible) {
                child.renderOrder = renderOrder;
            }
        });

        // Retourner l'objet modifié pour permettre le chaînage
        return object;
    }
}